<template>
  <v-container fluid>
    <v-form ref="form" v-model="isValid" lazy-validation>
      <vite-module-container :moduleNames="['offerAndTask']">
        <v-layout>
          <v-flex class="headline">{{ $t('offerSettings') }}</v-flex>
        </v-layout>
        <v-divider></v-divider>
        <v-container>
          <v-flex md3 lg2>
            <v-text-field
              :label="$t('defaultPendingPeriod')"
              v-model.number="settings.pendingPeriod"
              class="required_field"
              :rules="[fieldIsRequired('pending period'), fieldIsInteger('pending period'), fieldIsNoLessThanZero('pending period')]"
              :suffix="$t('days')"></v-text-field>
          </v-flex>
          <v-flex md3 lg2>
            <v-text-field
              :label="$t('minimumPaymentAmount')"
              v-model.number="settings.minPaymentAmount"
              class="required_field"
              :rules="[fieldIsRequired('pending period'), fieldIsNoLessThanZero('pending period')]"
              prefix="$"></v-text-field>
          </v-flex>
          <v-flex md4 lg3>
            <v-checkbox
              :label="$t('emailNotificationForNewComments')"
              v-model="settings.recieveEmailfromComment"
              ></v-checkbox>
          </v-flex>
          <v-flex sm3><v-checkbox :label="$t('visibleToAllMembers')" v-model="settings.isOfferPublic"></v-checkbox></v-flex>
          <v-flex md6>
            <v-autocomplete
              ref="offerEditAutocomplete"
              @change="$refs.offerEditAutocomplete['lazySearch'] = ''"
              v-model="settings.defaultUsers"
              :items="members"
              item-text="name"
              item-value="key"
              :label="$t('defaultUsers')"
              chips
              deletable-chips
              multiple
              return-object
              prepend-inner-icon="filter_list"
              :loading="$store.getters.loadingMembers"
            >
              <template v-slot:item="data">
                <template v-if="isObject(data.item)">
                  <v-list-tile-content v-text="data.item"></v-list-tile-content>
                </template>
                <template v-else>
                  <v-list-tile-content>
                    <v-list-tile-title>
                      <v-icon v-if="data.item.isGroup">group</v-icon>
                      <v-icon v-else>person</v-icon>
                      {{data.item.name}}
                    </v-list-tile-title>
                  </v-list-tile-content>
                </template>
              </template>
            </v-autocomplete>
          </v-flex>
        </v-container>
      </vite-module-container>
      <vite-module-container :moduleNames="['label', 'shipment']">
        <v-layout>
            <v-flex class="headline">{{ $t('outboundSettings') }}</v-flex>
        </v-layout>
        <v-divider></v-divider>
        <v-container>
          <v-flex md4 lg3>
            <SelectWidget
              :label="$t('otherServices')"
              :items="otherServices"
              v-model="settings.defaultSelectedOtherServices"
              itemText="displayName"
              itemValue="value"
              chipText="displayName"
              :displayQty="3"
              :mutuallyExclusives="['photo', 'photoForItem']"
              hide-details
            />
          </v-flex>
        </v-container>
      </vite-module-container>
      <v-layout>
        <v-flex class="headline">{{ $t('otherSettings') }}</v-flex>
      </v-layout>
      <v-divider></v-divider>
      <v-container>
        <v-flex md4 lg3 v-if="settings.recieveEmailfromComment">
          <v-text-field
            :label="$t('emailAddressForPaymentRequestComments')"
            v-model="settings.paymentRequestRecieveEmail"
          ></v-text-field>
        </v-flex>
        <v-flex md4 lg3 v-if="settings.recieveEmailfromComment">
          <v-text-field
            :label="$t('emailAddressForProposedOfferComments')"
            v-model="settings.proposeOfferRecieveEmail"
          ></v-text-field>
        </v-flex>
        <v-flex md4 lg3>
          <v-switch
            :label="settings.isMeasurementMetric ? $t('metric(kg/cm)') : $t('imperial(lbs/inch)')"
            v-model="settings.isMeasurementMetric"></v-switch>
        </v-flex>
        <v-flex md4 lg3>
          <v-icon color="primary">info</v-icon> {{ $t('metricSystemIsOnlyForInputData') }}
        </v-flex>
        <v-flex md4 lg3>
          <v-checkbox
            :label="$t('enableAmazonSites')"
            v-model="settings.enableAmazonSites"
            ></v-checkbox>
        </v-flex>
        <v-flex md4 lg3>
          <v-checkbox
            :label="$t('isRenameLabelByOrderKey')"
            v-model="settings.isRenameLabelByOrderKey"
            ></v-checkbox>
        </v-flex>
        <v-flex md4 lg3>
          <v-checkbox
            :label="$t('isAutoMarkShipment')"
            v-model="settings.isAutoMarkShipment"
            ></v-checkbox>
        </v-flex>
        <v-flex md6>
          <ETDSelector
            v-model="selectedLetterhead"
            :label="$t('signatureImage')"
            :placeholder="$t('selectOrUploadLetterhead')"
            containerWidth="800px"
            @imageUploaded="handleImageUploaded"
            @imageDeleted="handleImageDeleted"
            @change="handleImageChange"
          />
        </v-flex>
        <template>
          <vite-module-container :moduleNames="['monthEndClose']">
            <v-flex md4 lg3>
              <v-checkbox
                :label="$t('autoMonthEndClose')"
                v-model="settings.isAutoMonthEndClose"
                ></v-checkbox>
            </v-flex>
          </vite-module-container>
        </template>
        <v-flex md3 lg2>
          <v-btn color="primary" @click="onSubmitted">{{ $t('save') }}</v-btn>
        </v-flex>
      </v-container>
    </v-form>
  </v-container>
</template>

<script>
import { checkRules, isObject } from '@/utils/tools'
import SelectWidget from './SelectWidget.vue'
import ETDSelector from './ETDSelector.vue'
export default {
  name: 'GeneralSettings',
  data () {
    return {
      settings: {
        pendingPeriod: 0,
        minPaymentAmount: 0,
        recieveEmailfromComment: false,
        paymentRequestRecieveEmail: undefined,
        proposeOfferRecieveEmail: undefined,
        defaultUsers: [],
        isMeasurementMetric: false,
        isAutoMonthEndClose: false,
        enableAmazonSites: false,
        isRenameLabelByOrderKey: false,
        isAutoMarkShipment: false,
        defaultSelectedOtherServices: []
      },
      selectedLetterhead: null,
      isValid: true
    }
  },
  components: {SelectWidget, ETDSelector},
  mixins: [checkRules],
  mounted () {
    Object.keys(this.settings).forEach(key => {
      this.settings[key] = this.tenantLimitedInfo[key] === undefined ? this.settings[key] : this.tenantLimitedInfo[key]
    })
    // 缓存初始设置
    this.cacheIsAutoMonthEndCloseSetting = this.settings.isAutoMonthEndClose
  },
  watch: {
    'settings.recieveEmailfromComment': function (value) {
      if (value) {
        this.settings.proposeOfferRecieveEmail = this.settings.proposeOfferRecieveEmail !== undefined ? this.settings.proposeOfferRecieveEmail : this.tenantEmail
        this.settings.paymentRequestRecieveEmail = this.settings.paymentRequestRecieveEmail !== undefined ? this.settings.proposeOfferRecieveEmail : this.tenantEmail
      }
    }
  },
  computed: {
    tenantLimitedInfo () {
      return this.$store.getters.tenantLimitedInfo
    },
    tenantEmail () {
      return this.$store.getters.tenant.email
    },
    members () {
      let groups = this.$store.getters.groups.map(item => { let {name, groupKey: key} = item; return {name, key, isGroup: true} })
      let members = [{ header: 'Groups' }, ...groups, { divider: true }, { header: 'Users' }]
      let activeUsers = this.$store.getters.users.filter(user => user.approvalType === 3)
      activeUsers.forEach(item => {
        let {name, uid: key} = item
        members.push({name, key, isGroup: false})
      })
      return members
    },
    otherServices () {
      return this.$store.getters.otherServices
    },
    env () {
      return process.env.NODE_ENV
    }
  },
  methods: {
    isObject,
    handleImageUploaded (imageData) {
      console.log('Image uploaded:', imageData)
      this.$emit('image-uploaded', imageData)
    },
    handleImageDeleted (imageId) {
      console.log('Image deleted:', imageId)
      this.$emit('image-deleted', imageId)
    },
    handleImageChange (image) {
      console.log('Image changed:', image)
      this.$emit('image-changed', image)
    },
    onSubmitted () {
      if (!this.$refs.form.validate()) {
        this.$store.dispatch('showToast', {info: this.$t('Form validate failed.'), level: 'error'})
        return Promise.reject(Error('Form validate failed.'))
      }
      return this.submitSettings()
    },
    submitSettings () {
      let payload = {...this.settings}
      // 判断是否更新 auto month-end close 设置，是否需要立即执行一次
      if (this.settings.isAutoMonthEndClose !== this.cacheIsAutoMonthEndCloseSetting) payload.isNeedExecuteMonthEndClose = true
      if (!payload.recieveEmailfromComment) {
        delete payload.paymentRequestRecieveEmail
        delete payload.proposeOfferRecieveEmail
      }
      this.dispatchAndToast(this.$store.dispatch('changeTenantLimitedInfo', payload), 'Settings updated').then(() => {
        this.cacheIsAutoMonthEndCloseSetting = this.settings.isAutoMonthEndClose
      })
    },
    dispatchAndToast (promise, actionText) {
      return promise
        .then(rtn => {
          this.$store.dispatch('showToast', {info: `${actionText} ${this.$t('successfully')}`, level: 'success'})
        })
        .catch(error => {
          this.$store.dispatch('showToast', {info: `${actionText} ${this.$t('failed')}` + error.message})
        })
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/components/GeneralSettings.vue